import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { Permission } from "@ir/types";
import { LoadingState } from "@ir/ui";
import { useAuth } from "./AuthProvider";

/**
 * Gate for every admin route.
 *
 * A user who lacks the permission is sent to /no-access rather than shown a
 * disabled page — the guide asks that admin-only pages render nothing useful to
 * unauthorised users, and a greyed-out screen still leaks its structure.
 */
export function RequireAuth({
  children,
  permission,
}: {
  children: ReactNode;
  permission?: Permission;
}) {
  const { status, can } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <LoadingState message="Checking your access…" className="min-h-dvh" />;
  }

  if (status === "unauthenticated") {
    return (
      <Navigate
        to="/sign-in"
        replace
        state={{ returnTo: location.pathname + location.search }}
      />
    );
  }

  if (permission && !can(permission)) {
    return <Navigate to="/no-access" replace />;
  }

  return <>{children}</>;
}

/** Renders children only when the user holds the permission. */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useAuth();
  return <>{can(permission) ? children : fallback}</>;
}
